/**
 * Module 35 (WP-35.6) — free-slide solver.
 *
 * A BFS over "slide a block that way until something stops it" moves on the
 * WP-35.4/35.5 model: 4-way for free blocks, single-axis for arrow-locked ones,
 * cell-set collision, width-matched gate exits. Returns the SHORTEST move sequence
 * that clears the board (every block exited), or `null` if unsolvable within
 * `maxNodes`.
 *
 * "Until something stops it" includes a gate the block can go through (WP-50.4):
 * a slide ends the moment the block draws level with one, so it can never travel
 * past its own gate. Before that the slide ran to the wall and the gate was
 * checked once at the end, which made every par this solver reported describe a
 * harder game than the one the board actually plays — AUD-010.
 *
 * Shortest matters: the difficulty score (WP-35.9) reads the optimal move count,
 * and BFS by move-depth gives that.
 *
 * PERFORMANCE (the make-or-break for this module): the naive `Set<string>`
 * version hit ~11 nodes/ms on an 8×8 and blew the ~300ms generation budget by
 * ~40×. This implementation flattens everything to integers:
 *   - the board is a `Uint8Array(G*G)` occupancy grid rebuilt once per node;
 *   - each block is a fixed slot with precomputed cell offsets + per-direction
 *     a matching-gate table;
 *   - a state is the blocks' anchor cell-indexes (removed = sentinel) hashed into
 *     a compact `String.fromCharCode` key — no template literals in the hot loop.
 * Combined with a `maxNodes` cap (the band's tuned `solverLimit`) an unsolvable/
 * huge board fails fast instead of hanging. `free-solver.test.ts` replays every
 * solution through the CANONICAL `free-move` helpers, so this fast path can't
 * silently diverge from the validated movement rules.
 *
 * This is a DISTINCT implementation from the legacy single-axis solver module
 * (which stays the live game's solver until the generator migrates to the
 * free-slide model in WP-35.8); it does not import it.
 */
import { poweredPlateIds } from '../model/power';
import { canMoveInDirection, occupiedCells } from '../model/shape';
import { exitingGate, occupiedKeysExcept } from '../model/free-move';
const DIRS = ['up', 'down', 'left', 'right'];
const DROW = { up: -1, down: 1, left: 0, right: 0 };
const DCOL = { up: 0, down: 0, left: -1, right: 1 };
const REMOVED = 0xffff; // anchor-index sentinel for an exited block
/** A gate with no `powered` field: open on every node. */
const ALWAYS_OPEN = -1;
/** A gate whose `powered.plateId` names no plate: closed on every node. */
const NEVER_OPEN = -2;
/**
 * Shared setup for both searches: strip blocks already at a gate, apply the
 * colour pre-prune, and flatten each remaining block into a fixed `Slot` with
 * precomputed offsets, legal directions and matching gates.
 */
function prepare(blocks, exits, gridSize, rainbow, 
/**
 * Drop blocks that are already at a gate before the search starts. Always true
 * in the game; {@link solverExitsForTest} turns it off, because the strip uses
 * the CANONICAL predicate and would otherwise answer the question the seam
 * exists to ask the solver's own copy.
 */
strip = true, plates = [], surge = false) {
    const G = gridSize;
    const plateCells = Int32Array.from(plates, (p) => p.cell.row * G + p.cell.col);
    const plateIndex = new Map(plates.map((p, i) => [p.id, i]));
    const empty = (decided) => ({
        decided,
        plateCells,
        slots: [],
        start: new Uint16Array(0),
        G,
    });
    // The strip runs at the START state, so the power set is the one that holds
    // there — computed from every block, including the one being tested, because
    // a block standing on its own gate's plate really does open it.
    const powered = poweredPlateIds(blocks, plates);
    const present = strip
        ? blocks.filter((b) => !exitsNow(b, blocks, exits, gridSize, rainbow, powered, surge))
        : blocks;
    if (present.length === 0)
        return empty(true);
    // Colour pre-prune: a block whose colour has no gate can never exit.
    const openGate = exits.some((e) => e.colorId === undefined);
    const gatedColours = new Set(exits.map((e) => e.colorId).filter((c) => !!c));
    if (!rainbow && !openGate && !present.every((b) => gatedColours.has(b.colorId))) {
        return empty(false);
    }
    const slots = present.map((b) => {
        const offs = occupiedCells({ ...b, position: { row: 0, col: 0 } }).map((c) => ({
            dr: c.row,
            dc: c.col,
        }));
        const gates = exits
            // Module 52 (WP-52.4) — the axis clause, mirroring `exitsThroughGate`.
            // A block leaves by continuing through the gate, so a gate on an axis its
            // arrows forbid is not a gate it can use. Filtered once per slot here
            // rather than tested per node: `movementConstraint` cannot change during a
            // search, and this is the same reason `dirs` below is precomputed.
            //
            // Missing this is not a cosmetic divergence. The solver decides which
            // boards the generator is allowed to ship and what par is, so a solver
            // that believes in an exit the game refuses hands the player a level whose
            // solution does not work — the AUD-010 shape, from the opposite side.
            .filter((e) => canMoveInDirection(b, e.direction))
            .filter((e) => rainbow || e.colorId === undefined || e.colorId === b.colorId)
            .map((e) => {
            const horizontal = e.direction === 'left' || e.direction === 'right';
            const wall = e.direction === 'right' || e.direction === 'down' ? G - 1 : 0;
            const span = e.span ?? 1;
            const start = horizontal ? e.position.row : e.position.col;
            const lanes = new Set();
            for (let i = 0; i < span; i++)
                lanes.add(start + i);
            // `dr`/`dc` are Module 53's clearance walk — the direction the block
            // sweeps out through this opening.
            // Module 63 — resolved to an INDEX, never to a yes/no. A powered gate
            // is deliberately NOT filtered out here the way an axis- or
            // colour-mismatched one is: those cannot change during a search, and a
            // closed gate can open on the very next node.
            const plateIdx = !e.powered
                ? ALWAYS_OPEN
                : (plateIndex.get(e.powered.plateId) ?? NEVER_OPEN);
            return {
                dir: e.direction,
                wall,
                lanes,
                horizontal,
                dr: DROW[e.direction],
                dc: DCOL[e.direction],
                plateIdx,
            };
        });
        return { id: b.id, offs, dirs: DIRS.filter((d) => canMoveInDirection(b, d)), gates };
    });
    const start = new Uint16Array(slots.length);
    present.forEach((b, s) => (start[s] = b.position.row * G + b.position.col));
    return { decided: null, plateCells, slots, start, G };
}
export function solveFree(blocks, exits, gridSize, options = {}) {
    const maxNodes = options.maxNodes ?? 100000;
    const maxDepth = options.maxDepth ?? Infinity;
    const rainbow = options.rainbow ?? false;
    const plates = options.plates ?? [];
    const surge = options.surge ?? false;
    const G = gridSize;
    const idx = (row, col) => row * G + col;
    const prepared = prepare(blocks, exits, gridSize, rainbow, true, plates, surge);
    if (prepared.decided !== null) {
        return { moves: prepared.decided ? [] : null, nodes: 0, depthReached: 0 };
    }
    const { slots, start, plateCells } = prepared;
    const k = slots.length;
    const hashOf = (st) => String.fromCharCode(...st);
    const queue = [{ st: start, moves: [] }];
    // Head index instead of `shift()`: Array.shift is O(n), which made the whole
    // BFS O(n²) and dominated generation time at 6×6 and above.
    let head = 0;
    const visited = new Set([hashOf(start)]);
    const grid = new Uint8Array(G * G);
    let nodes = 0;
    let depthReached = 0;
    while (head < queue.length && nodes < maxNodes) {
        nodes++;
        const { st, moves } = queue[head++];
        if (moves.length > depthReached)
            depthReached = moves.length;
        // Paint the occupancy grid for this state once.
        grid.fill(0);
        for (let s = 0; s < k; s++) {
            if (st[s] === REMOVED)
                continue;
            const row = (st[s] / G) | 0;
            const col = st[s] - row * G;
            const offs = slots[s].offs;
            for (let o = 0; o < offs.length; o++)
                grid[(row + offs[o].dr) * G + (col + offs[o].dc)] = s + 1;
        }
        for (let s = 0; s < k; s++) {
            if (st[s] === REMOVED)
                continue;
            const slot = slots[s];
            const self = s + 1;
            const row0 = (st[s] / G) | 0;
            const col0 = st[s] - row0 * G;
            for (const dir of slot.dirs) {
                const dr = DROW[dir];
                const dc = DCOL[dir];
                let row = row0;
                let col = col0;
                let moved = false;
                let exited = false;
                // Module 53 — the at-rest exit, mirroring `slideAndExitAgainst`. A block
                // already against the wall cannot take a step, so without this the loop
                // below never runs and the gate is never asked about. See the model for
                // the measurement (`Bugs/Rainbow Block and Gate.mp4`, level 128).
                if (slotExits(slot, row0, col0, grid, G, self, plateCells, surge, dir)) {
                    moved = true;
                    exited = true;
                }
                // Slide until a wall, another block, or a gate this block can leave
                // through stops the whole rigid shape — whichever comes first. The gate
                // is asked about after EVERY step, not once at the end of the travel:
                // that granularity is the movement rule itself (WP-50.4 / AUD-010), and
                // it is what `model/free-move.ts#slideAndExitAgainst` does one cell at a
                // time. This loop stays a flat integer copy of it for speed — the
                // agreement is enforced by `free-solver.test.ts` replaying every solution
                // through the canonical helpers, not by sharing code.
                for (; !exited;) {
                    const nr = row + dr;
                    const nc = col + dc;
                    let blocked = false;
                    for (let o = 0; o < slot.offs.length; o++) {
                        const cr = nr + slot.offs[o].dr;
                        const cc = nc + slot.offs[o].dc;
                        if (cr < 0 || cr >= G || cc < 0 || cc >= G) {
                            blocked = true;
                            break;
                        }
                        const cell = grid[cr * G + cc];
                        if (cell !== 0 && cell !== self) {
                            blocked = true;
                            break;
                        }
                    }
                    if (blocked)
                        break;
                    row = nr;
                    col = nc;
                    moved = true;
                    if (slotExits(slot, row, col, grid, G, self, plateCells, surge)) {
                        exited = true;
                        break;
                    }
                }
                if (!moved)
                    continue;
                const next = st.slice();
                next[s] = exited ? REMOVED : idx(row, col);
                const hash = hashOf(next);
                if (visited.has(hash))
                    continue;
                visited.add(hash);
                const nextMoves = [...moves, { blockId: slot.id, direction: dir }];
                // Won when every slot is removed.
                let remaining = 0;
                for (let i = 0; i < k; i++)
                    if (next[i] !== REMOVED)
                        remaining++;
                if (remaining === 0)
                    return { moves: nextMoves, nodes, depthReached: nextMoves.length };
                if (nextMoves.length < maxDepth)
                    queue.push({ st: next, moves: nextMoves });
            }
        }
    }
    return { moves: null, nodes, depthReached };
}
/**
 * "Can this board be cleared at all?" — a depth-first search that stops at the
 * FIRST clearing sequence (WP-35.8).
 *
 * Why this exists next to `solveFree`: the generator only needs accept/reject,
 * but BFS-shortest is expensive precisely because it must exhaust every shallower
 * layer before it can prove a solution is optimal. Measured on 7×7/8×8 boards
 * with 5–6 blocks, BFS needed **30k–140k nodes** for the optimal answer, so every
 * rejected candidate cost that much too and generation ran into seconds. DFS with
 * exits tried first finds *a* clearing in a tiny fraction of that, which keeps
 * generation inside its budget. `solveFree` remains the source of truth wherever
 * the OPTIMAL move count matters (difficulty scoring, hints).
 *
 * Same movement rules, same slot machinery; only the frontier order differs.
 */
export function clearableFree(blocks, exits, gridSize, options = {}) {
    const maxNodes = options.maxNodes ?? 20000;
    const rainbow = options.rainbow ?? false;
    const plates = options.plates ?? [];
    const surge = options.surge ?? false;
    const prepared = prepare(blocks, exits, gridSize, rainbow, true, plates, surge);
    if (prepared.decided !== null) {
        return { clearable: prepared.decided, nodes: 0, depth: 0, firstMove: null };
    }
    const { slots, start, G, plateCells } = prepared;
    const k = slots.length;
    const idx = (row, col) => row * G + col;
    const hashOf = (st) => String.fromCharCode(...st);
    const stack = [{ st: start, depth: 0, first: null }];
    const visited = new Set([hashOf(start)]);
    const grid = new Uint8Array(G * G);
    let nodes = 0;
    while (stack.length > 0 && nodes < maxNodes) {
        nodes++;
        const { st, depth, first } = stack.pop();
        grid.fill(0);
        for (let s = 0; s < k; s++) {
            if (st[s] === REMOVED)
                continue;
            const row = (st[s] / G) | 0;
            const col = st[s] - row * G;
            const offs = slots[s].offs;
            for (let o = 0; o < offs.length; o++)
                grid[(row + offs[o].dr) * G + (col + offs[o].dc)] = s + 1;
        }
        // Collect this node's children, then push non-exiting ones first so the
        // EXITING moves end up on top of the stack: clearing a block always makes
        // progress, and trying those first is what makes this search cheap.
        const exiting = [];
        const shuffling = [];
        for (let s = 0; s < k; s++) {
            if (st[s] === REMOVED)
                continue;
            const slot = slots[s];
            const self = s + 1;
            const row0 = (st[s] / G) | 0;
            const col0 = st[s] - row0 * G;
            for (const dir of slot.dirs) {
                const dr = DROW[dir];
                const dc = DCOL[dir];
                let row = row0;
                let col = col0;
                let moved = false;
                let exited = false;
                // The at-rest exit, then the same per-step gate check as `solveFree`
                // above — see the comments there.
                if (slotExits(slot, row0, col0, grid, G, self, plateCells, surge, dir)) {
                    moved = true;
                    exited = true;
                }
                for (; !exited;) {
                    const nr = row + dr;
                    const nc = col + dc;
                    let blocked = false;
                    for (let o = 0; o < slot.offs.length; o++) {
                        const cr = nr + slot.offs[o].dr;
                        const cc = nc + slot.offs[o].dc;
                        if (cr < 0 || cr >= G || cc < 0 || cc >= G) {
                            blocked = true;
                            break;
                        }
                        const cell = grid[cr * G + cc];
                        if (cell !== 0 && cell !== self) {
                            blocked = true;
                            break;
                        }
                    }
                    if (blocked)
                        break;
                    row = nr;
                    col = nc;
                    moved = true;
                    if (slotExits(slot, row, col, grid, G, self, plateCells, surge)) {
                        exited = true;
                        break;
                    }
                }
                if (!moved)
                    continue;
                const next = st.slice();
                next[s] = exited ? REMOVED : idx(row, col);
                const hash = hashOf(next);
                if (visited.has(hash))
                    continue;
                visited.add(hash);
                // At the root this move IS the branch; deeper down the branch already
                // has one and it must not be overwritten.
                const childFirst = first ?? { blockId: slot.id, direction: dir };
                if (exited) {
                    let remaining = 0;
                    for (let i = 0; i < k; i++)
                        if (next[i] !== REMOVED)
                            remaining++;
                    if (remaining === 0) {
                        return { clearable: true, nodes, depth: depth + 1, firstMove: childFirst };
                    }
                    exiting.push({ st: next, depth: depth + 1, first: childFirst });
                }
                else {
                    shuffling.push({ st: next, depth: depth + 1, first: childFirst });
                }
            }
        }
        for (const frame of shuffling)
            stack.push(frame);
        for (const frame of exiting)
            stack.push(frame);
    }
    return { clearable: false, nodes, depth: 0, firstMove: null };
}
/**
 * Is this plate covered, with the moving block at (row, col)?
 *
 * **The composition trap this function exists to avoid.** The node's occupancy
 * grid holds the moving block at its STARTING cells — the slide loops never
 * repaint it, they skip `self` during the clearance walk instead. So reading the
 * grid alone would answer "was the plate covered before this block set off",
 * which is not the question. A block that began on a plate would go on powering
 * its gate after sliding away: a latch, and this module rejected latches by
 * name (CLAUDE.md decision 5).
 *
 * Coverage is therefore `(grid, excluding self) ∪ (mover's cells at row/col)`.
 * The `self` exclusion and the explicit offsets pass are two halves of one
 * answer; dropping either produces a plausible, wrong, quietly-green result —
 * the AUD-010 shape, where two callers share every primitive and compose them
 * differently.
 */
function plateCovered(cellIdx, offs, row, col, grid, G, self) {
    // Any OTHER block standing on it, read from the node grid.
    const occupant = grid[cellIdx];
    if (occupant !== 0 && occupant !== self)
        return true;
    // …or the mover itself, at the position being asked about rather than the one
    // the grid still remembers.
    for (let o = 0; o < offs.length; o++)
        if ((row + offs[o].dr) * G + (col + offs[o].dc) === cellIdx)
            return true;
    return false;
}
/**
 * Does the slot at anchor (row,col) exit through one of its gates?
 *
 * Module 53 — a flat integer copy of `model/free-move.ts#exitsThroughGate`'s
 * three geometry clauses: leading edge at the wall, every occupied lane inside
 * the opening, and a clear sweep out through it. It reads the node's occupancy
 * grid for the last one, which is why the grid is now threaded in.
 *
 * It stays a copy rather than a call for the reason the slide loop does: this
 * runs per node, up to 400k times per solve, and the canonical helper wants a
 * `Block` object and a `Set<string>` built per call. The agreement is enforced
 * instead by `free-solver.test.ts`, which replays every solution through the
 * canonical helpers, and by the Module 53 equivalence test that walks a corpus
 * of boards asking both forms the same question. Two implementations with a
 * proof they agree; not two implementations and a hope.
 *
 * `onlyDir` restricts the answer to gates on one wall — the at-rest check the
 * slide loops make before their first step, mirroring `slideAndExitAgainst`.
 */
function slotExits(slot, row, col, grid, G, self, plateCells, surge, onlyDir) {
    const offs = slot.offs;
    for (const g of slot.gates) {
        if (onlyDir !== undefined && g.dir !== onlyDir)
            continue;
        // Module 63 — the power clause, evaluated per node because it is the one
        // clause the search can change.
        if (g.plateIdx !== ALWAYS_OPEN && !surge) {
            if (g.plateIdx === NEVER_OPEN)
                continue;
            if (!plateCovered(plateCells[g.plateIdx], offs, row, col, grid, G, self))
                continue;
        }
        // Clauses 1 and 2 together, in one pass over the cells.
        let atWall = false;
        let lanesOk = true;
        for (let o = 0; o < offs.length; o++) {
            const cr = row + offs[o].dr;
            const cc = col + offs[o].dc;
            const travel = g.horizontal ? cc : cr;
            const lane = g.horizontal ? cr : cc;
            if (!g.lanes.has(lane)) {
                lanesOk = false;
                break;
            }
            if (travel === g.wall)
                atWall = true;
        }
        if (!lanesOk || !atWall)
            continue;
        // Clause 3 — nothing between any cell and the opening. Vacuous for a
        // rectangle, whose own cells fill the run; the L's notch lane is the case
        // that can fail.
        let clear = true;
        for (let o = 0; o < offs.length && clear; o++) {
            let cr = row + offs[o].dr + g.dr;
            let cc = col + offs[o].dc + g.dc;
            while (cr >= 0 && cc >= 0 && cr < G && cc < G) {
                const cell = grid[cr * G + cc];
                if (cell !== 0 && cell !== self) {
                    clear = false;
                    break;
                }
                cr += g.dr;
                cc += g.dc;
            }
        }
        if (clear)
            return true;
    }
    return false;
}
/**
 * Standalone "is this block already at a gate" check for the initial strip.
 *
 * Module 53 deleted the hand-written copy that used to live here and calls the
 * canonical predicate instead. This runs once per solve, not per node, so none
 * of the reasons `slotExits` stays a copy apply — and it was the third mirror of
 * the same rule, which is two more than AUD-010 should have left anybody
 * comfortable with.
 */
function exitsNow(block, blocks, exits, gridSize, rainbow, poweredIds, surge) {
    return (exitingGate(block, exits, gridSize, occupiedKeysExcept(blocks, block.id), {
        rainbow,
        poweredPlateIds: poweredIds,
        surge,
    }) !== null);
}
export const FreeSolver = {
    /** Convenience wrapper returning just the move list (or null). */
    solve(blocks, exits, gridSize, options = {}) {
        return solveFree(blocks, exits, gridSize, options).moves;
    },
};
/**
 * Module 53 (WP-53.6) — ask the SOLVER's exit rule about one board, for tests.
 *
 * The solver keeps a flat integer copy of the exit geometry (see
 * {@link slotExits}) because it runs per node and the canonical helper wants
 * objects. That is the AUD-010 shape by construction: two implementations of one
 * rule, which stayed in step for four modules and then did not.
 *
 * This is the seam that makes the agreement checkable rather than assumed. It
 * runs `block` through the solver's own `prepare` — the same colour and axis
 * filters, the same gate precompute — paints the same occupancy grid the search
 * paints, and returns what the search would conclude. `exit-rule-equivalence`
 * asks it and `exitsThroughGate` the same question over a corpus of boards and
 * requires the same answer.
 *
 * Exported for tests only; nothing in the game calls it.
 */
export function solverExitsForTest(block, blocks, exits, gridSize, rainbow = false, plates = [], surge = false) {
    // `strip: false` is the whole point. With the strip on, `prepare` removes any
    // block already at a gate — using the CANONICAL predicate — so the seam would
    // return "exits" without ever reaching `slotExits`, and would agree with the
    // model by construction in precisely the cases worth checking. Measured: with
    // the strip on, deliberately breaking `slotExits` left the L-rotation test
    // green.
    const prepared = prepare(blocks, exits, gridSize, rainbow, false, plates, surge);
    const slot = prepared.slots.find((s) => s.id === block.id);
    // The colour pre-prune decided the board without building slots. It is a
    // property of the whole board rather than of this block's geometry, so there
    // is nothing here for `slotExits` to answer.
    if (!slot)
        return false;
    const grid = new Uint8Array(gridSize * gridSize);
    const self = prepared.slots.indexOf(slot) + 1;
    prepared.slots.forEach((s, i) => {
        const b = blocks.find((x) => x.id === s.id);
        for (const c of occupiedCells(b))
            grid[c.row * gridSize + c.col] = i + 1;
    });
    return slotExits(slot, block.position.row, block.position.col, grid, gridSize, self, prepared.plateCells, surge);
}
